import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type CSSProperties,
} from 'react';
import styles from './fixedBackgroundScroll.module.css';

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

export interface FixedBgSectionRootProps
  extends ComponentPropsWithoutRef<'section'> {
  $mobileVh: number;
  $desktopVh: number;
  $fallbackBg?: string;
  $clipOverflow?: boolean;
}

export const FixedBgSectionRoot = forwardRef<
  HTMLElement,
  FixedBgSectionRootProps
>(function FixedBgSectionRoot(
  { className, style, $mobileVh, $desktopVh, $fallbackBg, $clipOverflow, ...rest },
  ref,
) {
  return (
    <section
      ref={ref}
      className={cx(styles.fixedBgSectionRoot, className)}
      data-clip-overflow={$clipOverflow ? 'true' : undefined}
      style={
        {
          ...style,
          '--fbg-mvh': $mobileVh,
          '--fbg-dvh': $desktopVh,
          ...($fallbackBg ? { '--fbg-fallback': $fallbackBg } : {}),
        } as CSSProperties
      }
      {...rest}
    />
  );
});

export const FixedBgLayer = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(function FixedBgLayer({ className, ...rest }, ref) {
  return (
    <div ref={ref} className={cx(styles.fixedBgLayer, className)} {...rest} />
  );
});

export interface FixedBgImageWrapProps extends ComponentPropsWithoutRef<'div'> {
  $fallback: string;
}

export const FixedBgImageWrap = forwardRef<
  HTMLDivElement,
  FixedBgImageWrapProps
>(function FixedBgImageWrap({ className, style, $fallback, ...rest }, ref) {
  return (
    <div
      ref={ref}
      className={cx(styles.fixedBgImageWrap, className)}
      style={{ ...style, '--fbg-wrap-fallback': $fallback } as CSSProperties}
      {...rest}
    />
  );
});

export const FixedBgOverlay = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(function FixedBgOverlay({ className, ...rest }, ref) {
  return (
    <div ref={ref} className={cx(styles.fixedBgOverlay, className)} {...rest} />
  );
});

export const FixedBgContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(function FixedBgContent({ className, ...rest }, ref) {
  return (
    <div ref={ref} className={cx(styles.fixedBgContent, className)} {...rest} />
  );
});

export interface FixedBgVhSpacerProps extends ComponentPropsWithoutRef<'div'> {
  $mobileVh: number;
  $desktopVh: number;
}

export const FixedBgVhSpacer = forwardRef<HTMLDivElement, FixedBgVhSpacerProps>(
  function FixedBgVhSpacer(
    { className, style, $mobileVh, $desktopVh, ...rest },
    ref,
  ) {
    return (
      <div
        ref={ref}
        className={cx(styles.fixedBgVhSpacer, className)}
        style={
          { ...style, '--fbg-mvh': $mobileVh, '--fbg-dvh': $desktopVh } as CSSProperties
        }
        {...rest}
      />
    );
  },
);

export const FixedBgTextStage = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(function FixedBgTextStage({ className, ...rest }, ref) {
  return (
    <div
      ref={ref}
      className={cx(styles.fixedBgTextStage, className)}
      {...rest}
    />
  );
});

export const FixedBgViewportContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(function FixedBgViewportContent({ className, ...rest }, ref) {
  return (
    <div
      ref={ref}
      className={cx(styles.fixedBgViewportContent, className)}
      {...rest}
    />
  );
});
